import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Avatar, Alert,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  Autocomplete, TextField, Snackbar, IconButton, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CampaignIcon from '@mui/icons-material/Campaign';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import db from '../../data/demoDatabase';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

export default function CampaignSettings() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [dirtyMap, setDirtyMap] = useState({}); // campaignId → participantIds draft

  const campaigns = useMemo(() => db.campaigns.getAll(), [refreshKey]);

  // Get all partners + individual agents for the assignment picker
  const allParticipants = useMemo(() => {
    const partners = db.users.getByRole('partner').map((u) => ({
      id: u.id, name: u.name, role: 'partner', label: `${u.name} (Partner)`,
    }));
    const agents = db.users.getByRole('individual_agent').map((u) => ({
      id: u.id, name: u.name, role: 'individual_agent', label: `${u.name} (Agent)`,
    }));
    return [...partners, ...agents];
  }, [refreshKey]);

  const getDraftParticipants = (campaign) => {
    const ids = dirtyMap[campaign.id] ?? campaign.participantIds ?? [];
    return ids;
  };

  const handleParticipantChange = (campaignId, newIds) => {
    setDirtyMap((prev) => ({ ...prev, [campaignId]: newIds }));
  };

  const handleSave = (campaignId) => {
    const ids = dirtyMap[campaignId];
    if (!ids) return;
    db.campaigns.update(campaignId, { participantIds: ids });
    db.auditLogs.create({
      userId: 'SYSTEM',
      userName: 'Admin',
      userRole: 'admin',
      action: 'update_campaign_participants',
      entityType: 'campaign',
      entityId: campaignId,
      description: `Updated campaign participants (${ids.length} assigned)`,
    });
    setDirtyMap((prev) => {
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
    setRefreshKey((k) => k + 1);
    setSnack({ open: true, msg: 'Campaign participants saved', severity: 'success' });
  };

  const handleRemoveParticipant = (campaignId, userId) => {
    const current = getDraftParticipants(campaigns.find((c) => c.id === campaignId));
    handleParticipantChange(campaignId, current.filter((id) => id !== userId));
  };

  const statusColor = (status) => {
    if (status === 'active') return { bg: '#E8F5E9', color: '#2E7D32' };
    if (status === 'ended') return { bg: '#ECEFF1', color: '#546E7A' };
    return { bg: '#FFF8E1', color: '#F57F17' };
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ backgroundColor: PRIMARY, width: 48, height: 48 }}>
          <SettingsIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            Campaign Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure which Partners and Individual Agents can participate in each campaign
          </Typography>
        </Box>
      </Box>

      {/* Stats bar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: PRIMARY }}>
              {campaigns.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Total Campaigns</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: SECONDARY }}>
              {campaigns.filter((c) => c.status === 'active').length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Active</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#90A4AE' }}>
              {allParticipants.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">Available Participants</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Campaign list */}
      {campaigns.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>No campaigns found.</Alert>
      ) : (
        campaigns.map((campaign) => {
          const sc = statusColor(campaign.status);
          const currentIds = getDraftParticipants(campaign);
          const isDirty = !!dirtyMap[campaign.id];
          const assignedUsers = currentIds
            .map((id) => allParticipants.find((p) => p.id === id))
            .filter(Boolean);
          const partners = assignedUsers.filter((u) => u.role === 'partner');
          const agents = assignedUsers.filter((u) => u.role === 'individual_agent');

          return (
            <Accordion key={campaign.id} defaultExpanded={campaign.status === 'active'}
              sx={{ mb: 1.5, borderRadius: '12px !important', overflow: 'hidden',
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}>
                <Avatar sx={{ backgroundColor: sc.bg, width: 36, height: 36 }}>
                  <CampaignIcon sx={{ color: sc.color, fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY }}>
                      {campaign.name}
                    </Typography>
                    <Chip label={campaign.status.toUpperCase()} size="small"
                      sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    {isDirty && (
                      <Chip label="UNSAVED" size="small"
                        sx={{ backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Code: {campaign.code} | {campaign.startDate} → {campaign.endDate} | {currentIds.length} participant{currentIds.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 2 }} />

                {/* Participant assignment Autocomplete */}
                <Autocomplete
                  multiple
                  options={allParticipants}
                  getOptionLabel={(o) => o.label}
                  value={assignedUsers}
                  onChange={(_, newValue) =>
                    handleParticipantChange(campaign.id, newValue.map((v) => v.id))
                  }
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Assign Partners & Agents"
                      placeholder="Search by name..." />
                  )}
                  renderTags={() => null} // render custom chips below instead
                  sx={{ mb: 2 }}
                />

                {/* Grouped display: Partners */}
                {partners.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#3182CE', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                      <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      Partners ({partners.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {partners.map((p) => (
                        <Chip key={p.id} label={p.name} size="small"
                          icon={<PersonIcon sx={{ fontSize: 14 }} />}
                          onDelete={() => handleRemoveParticipant(campaign.id, p.id)}
                          sx={{ fontWeight: 600, backgroundColor: '#E3F2FD', color: '#1565C0' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Grouped display: Individual Agents */}
                {agents.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7B1FA2', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                      <BadgeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      Individual Agents ({agents.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {agents.map((a) => (
                        <Chip key={a.id} label={a.name} size="small"
                          icon={<BadgeIcon sx={{ fontSize: 14 }} />}
                          onDelete={() => handleRemoveParticipant(campaign.id, a.id)}
                          sx={{ fontWeight: 600, backgroundColor: '#F3E5F5', color: '#7B1FA2' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {currentIds.length === 0 && (
                  <Alert severity="info" sx={{ borderRadius: 2, mb: 1.5 }}>
                    No participants assigned to this campaign yet.
                  </Alert>
                )}

                {/* Save button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="contained" size="small" startIcon={<SaveIcon />}
                    disabled={!isDirty}
                    onClick={() => handleSave(campaign.id)}
                    sx={{
                      borderRadius: 2, textTransform: 'none', fontWeight: 600,
                      backgroundColor: PRIMARY,
                      '&:hover': { backgroundColor: '#142847' },
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
